import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useConsultation } from "../../context/ConsultationContext";
import CountryPhone from "./CountryPhone";
import { buildFullPhone, DEFAULT_COUNTRY } from "../../lib/countries";

const empty = {
  name: "", email: "", phone: "", country: DEFAULT_COUNTRY, company: "",
  interested_demo: "", preferred_time: "", message: "",
};

export const ConsultationDialog = () => {
  const { open, setOpen, prefill } = useConsultation();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, ...prefill });
      setDone(false);
    }
  }, [open, prefill]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/consultations", {
        ...form,
        phone: buildFullPhone(form.country, form.phone),
      });
      setDone(true);
      toast.success("Consultation request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="consultation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Book a free consultation</DialogTitle>
          <DialogDescription>
            Tell us about your business — a StaffLink specialist will reach out within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 flex flex-col items-center text-center gap-3" data-testid="consultation-success">
            <CheckCircle2 className="w-14 h-14 text-accent" />
            <p className="font-display text-xl">You're on the list, {form.name.split(" ")[0]}!</p>
            <p className="text-muted-foreground">
              We've received your request{form.interested_demo ? ` about ${form.interested_demo}` : ""}. Expect a WhatsApp message from a StaffLink specialist soon.
            </p>
            <Button onClick={() => setOpen(false)} data-testid="consultation-close">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="c-name">Full name *</Label>
                <Input id="c-name" data-testid="consultation-name" value={form.name} onChange={update("name")} placeholder="Ama Boateng" />
              </div>
              <div>
                <Label htmlFor="c-company">Company</Label>
                <Input id="c-company" data-testid="consultation-company" value={form.company} onChange={update("company")} placeholder="Acme Ltd" />
              </div>
              <div>
                <Label htmlFor="c-email">Email *</Label>
                <Input id="c-email" type="email" data-testid="consultation-email" value={form.email} onChange={update("email")} placeholder="you@company.com" />
              </div>
              <div className="sm:col-span-2">
                <Label>Phone *</Label>
                <CountryPhone
                  testIdPrefix="consultation-phone"
                  country={form.country}
                  phone={form.phone}
                  onChange={({ country, phone }) => setForm((f) => ({ ...f, country, phone }))}
                />
              </div>
              <div>
                <Label htmlFor="c-demo">Interested in</Label>
                <Input id="c-demo" data-testid="consultation-demo" value={form.interested_demo} onChange={update("interested_demo")} placeholder="e.g. Lead Capture & CRM" />
              </div>
              <div>
                <Label htmlFor="c-time">Preferred time</Label>
                <Input id="c-time" data-testid="consultation-time" value={form.preferred_time} onChange={update("preferred_time")} placeholder="Weekday morning" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-msg">What would you like to automate?</Label>
              <Textarea id="c-msg" data-testid="consultation-message" rows={3} value={form.message} onChange={update("message")} placeholder="Share a bit about your business…" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="consultation-submit">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send request
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationDialog;
